import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from 'src/@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/@/components/ui/table';
import Iconify from 'src/components/Iconify';
import { CRM_TAG_COLOR_OPTIONS, getCrmTagColor } from 'src/utils/crmTagColors';
import { FUNIL_DESIGN as D } from './FunilComponent.design';

import v2_actionListLeadTags from 'src/actions/v2/crm/v2_actionListLeadTags';
import v2_actionAddLeadTag from 'src/actions/v2/crm/v2_actionAddLeadTag';
import v2_actionEditLeadTag from 'src/actions/v2/crm/v2_actionEditLeadTag';
import v2_actionDeleteLeadTag from 'src/actions/v2/crm/v2_actionDeleteLeadTag';

const EMPTY = { _id: null, nome: '', cor_hex: 'slate' };

export default function TabLeadTags({ cliente, usuarioId }) {
  const { t } = useTranslation('settings');

  const [tags, setTags] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [salvando, setSalvando] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    buscarTags();
  }, []);

  async function buscarTags() {
    try {
      const result = await v2_actionListLeadTags(cliente._id);
      if (result.status === 200 && !result.data?.error) {
        setTags(result.data.data || []);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function handleSalvar() {
    if (!form.nome?.trim()) return;
    try {
      setSalvando(true);
      const scope = { nome: form.nome.trim(), cor_hex: form.cor_hex, criado_por: usuarioId || null };
      if (form._id) {
        await v2_actionEditLeadTag(form._id, { scope });
      } else {
        await v2_actionAddLeadTag(cliente._id, { scope });
      }
      await buscarTags();
      setOpen(false);
      setForm(EMPTY);
    } catch (error) {
      console.log(error);
    } finally {
      setSalvando(false);
    }
  }

  async function handleDeletar() {
    try {
      await v2_actionDeleteLeadTag(confirmDelete._id);
      await buscarTags();
      setConfirmDelete(null);
    } catch (error) {
      console.log(error);
      setConfirmDelete(null);
    }
  }

  const corSelecionada = getCrmTagColor(form.cor_hex);

  return (
    <div style={{ fontFamily: D.fontStack }}>
      <div className={D.sectionHeader}>
        <div className={D.sectionHeaderMain}>
          <h2 className={D.sectionHeaderTitle}>
            {t('funnel.leadTags.sectionTitle', 'Tags de Negociação')}
          </h2>
          <p className={D.sectionHeaderSub}>
            {t('funnel.leadTags.sectionHint', 'Etiquetas aplicadas ao card do funil, independentes das tags do chat')}
          </p>
        </div>
        <button
          type="button"
          className={D.primaryButton}
          onClick={() => {
            setForm(EMPTY);
            setOpen(true);
          }}
        >
          {t('buttons.create', { ns: 'common' })}
        </button>
      </div>

      <div className={D.cardFlush}>
        {tags.length === 0 ? (
          <div className={D.emptyState}>
            <p className={D.emptyDesc}>
              {t('funnel.leadTags.emptyState', 'Nenhuma tag de negociação foi cadastrada')}
            </p>
          </div>
        ) : (
          <div className={D.tableWrap}>
            <Table>
            <TableHeader>
              <TableRow className="border-b border-[#f0f0f3] hover:bg-transparent">
                <TableHead className={D.tableHead}>{t('labels.name', { ns: 'common' })}</TableHead>
                <TableHead className={`${D.tableHead} text-right`}>{t('buttons.actions', { ns: 'common' })}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tags.map((tag) => {
                const cor = getCrmTagColor(tag.cor_hex);
                return (
                  <TableRow key={tag._id} className={D.tableRow}>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[13px] font-semibold ${cor.pill}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${cor.dot}`} />
                        {tag.nome}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1.5">
                        <button
                          type="button"
                          className={D.iconButton}
                          onClick={() => {
                            setForm({ _id: tag._id, nome: tag.nome, cor_hex: tag.cor_hex || 'slate' });
                            setOpen(true);
                          }}
                        >
                          <Iconify icon="eva:edit-2-outline" width={14} />
                        </button>
                        <button
                          type="button"
                          className={D.iconButtonDanger}
                          onClick={() => setConfirmDelete(tag)}
                        >
                          <Iconify icon="eva:trash-2-outline" width={14} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[425px]" style={{ fontFamily: D.fontStack }}>
          <DialogHeader>
            <DialogTitle className={D.dialogTitle}>
              {form._id
                ? t('funnel.leadTags.editTitle', 'Editar tag de negociação')
                : t('funnel.leadTags.createTitle', 'Nova tag de negociação')}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-5 py-2">
            <div className={D.field}>
              <label className={D.label}>{t('labels.name', { ns: 'common' })}</label>
              <Input
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                className={D.input}
              />
            </div>
            <div className={D.field}>
              <label className={D.label}>{t('tags.modal.colorLabel', 'Cor')}</label>
              <Select value={form.cor_hex} onValueChange={(v) => setForm({ ...form, cor_hex: v })}>
                <SelectTrigger className={D.select}>
                  <SelectValue>
                    <div className="flex items-center gap-2">
                      <div className={`w-5 h-5 rounded border border-[#d4d4d8] ${corSelecionada.swatch}`} />
                      <span>{corSelecionada.nome}</span>
                    </div>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {CRM_TAG_COLOR_OPTIONS.map((cor) => (
                    <SelectItem key={cor.value} value={cor.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-5 rounded border border-[#d4d4d8] flex-shrink-0 ${cor.swatch}`} />
                        <span>{cor.nome}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="px-3.5 py-3 rounded-lg border border-[#e7e7ec] bg-[#fafafb]">
              <p className="text-[12px] text-[#a1a1aa] mb-2">Prévia</p>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[13px] font-semibold ${corSelecionada.pill}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${corSelecionada.dot}`} />
                {form.nome || t('funnel.leadTags.previewPlaceholder', 'Nome da tag')}
              </span>
            </div>
          </div>
          <DialogFooter className={D.dialogFooter}>
            <button type="button" className={D.ghostButton} onClick={() => setOpen(false)}>
              {t('buttons.cancel', { ns: 'common' })}
            </button>
            <button
              type="button"
              className={D.primaryButton}
              onClick={handleSalvar}
              disabled={salvando || !form.nome?.trim()}
            >
              {form._id ? t('buttons.save', { ns: 'common' }) : t('buttons.create', { ns: 'common' })}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(confirmDelete)} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent className="sm:max-w-[425px]" style={{ fontFamily: D.fontStack }}>
          <DialogHeader>
            <DialogTitle className={D.dialogTitle}>{t('funnel.leadTags.deleteTitle', 'Excluir tag')}</DialogTitle>
          </DialogHeader>
          <p className={D.dialogDesc}>
            {t('funnel.leadTags.deleteHint', 'A tag será removida de todos os negócios que a utilizam. Essa ação não pode ser desfeita.')}
          </p>
          <DialogFooter className={D.dialogFooter}>
            <button type="button" className={D.ghostButton} onClick={() => setConfirmDelete(null)}>
              {t('buttons.cancel', { ns: 'common' })}
            </button>
            <button type="button" className={D.dangerButton} onClick={handleDeletar}>
              {t('buttons.delete', { ns: 'common' })}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
